import React from "react";
import { faSquareTerminal } from "@fortawesome/pro-regular-svg-icons";
import { Box, Flex } from "@singlestore/fusion/components/layout";
import { Label } from "@singlestore/fusion/components/typography";
import { cx } from "class-variance-authority";
import { useFlyout } from "contexts/FlyoutContext";
import md5 from "md5";
import { Language } from "prism-react-renderer";

import { Button } from "components/Button/Button";
import { FaIcon } from "components/FaIcon/FaIcon";
import { SyntaxBlock } from "components/SyntaxBlock/SyntaxBlock";

import { formatLanguage } from "utils/code-utils";
import { copyToClipboard } from "utils/copy-to-clipboard";
import { copyClipboardIcon } from "utils/custom-icons";
import { track } from "utils/segment";

type LanguageAlias = "shell" | "mongodb";

export type SupportedLanguage = Language | LanguageAlias | "text";

// shell should be mapped to bash for syntax highlighting only (display is
// managed in the formatLanguage function)
const LANGUAGE_HIGHLIGHT_MAPPING: Record<LanguageAlias, Language> = {
    shell: "bash",
    mongodb: "javascript",
};

type SyntaxBlockGroupProps = {
    inputBlock: string;
    outputBlock?: string;
    className?: string;
    language?: SupportedLanguage;
    interactive?: boolean;
};

// Supports an input and output block and includes the header
export function SyntaxBlockGroup({
    inputBlock,
    outputBlock,
    className,
    language = "text",
    interactive = false,
}: React.PropsWithChildren<SyntaxBlockGroupProps>) {
    let inputSection;
    let outputSection;

    const [copyLabelShown, setCopyLabelShown] = React.useState(false);

    const { setCode, setTab } = useFlyout();

    const lowercaseLanguage = language.toLowerCase();
    let highlightLanguage: Maybe<Language>;
    if (lowercaseLanguage in LANGUAGE_HIGHLIGHT_MAPPING) {
        highlightLanguage =
            LANGUAGE_HIGHLIGHT_MAPPING[lowercaseLanguage as LanguageAlias];
    } else if (language && language !== "text") {
        highlightLanguage = lowercaseLanguage as Language;
    }

    const uniqueId = md5(`${inputBlock}_${outputBlock}`);
    const visibleLanguage = language && language !== "text";

    if (inputBlock) {
        inputSection = (
            <SyntaxBlock
                className="input-block"
                language={highlightLanguage}
                code={inputBlock}
            />
        );
    }
    if (outputBlock) {
        outputSection = (
            <SyntaxBlock className="output-block" code={outputBlock} />
        );
    }

    const handleCopy = () => {
        copyToClipboard(inputBlock);
        setCopyLabelShown(true);

        setTimeout(() => {
            setCopyLabelShown(false);
        }, 2000);

        track("Copy Code Block", {
            elementContext: "Article Content",
            codeBlockId: uniqueId,
        });
    };

    const handlePlayground = () => {
        setCode(inputBlock);
        setTab("playground");

        track("Playground Session Initiated", {
            method: "Code Block",
            codeBlockId: uniqueId,
        });
    };

    let label;
    if (visibleLanguage) {
        label = (
            <Label
                className="selected-language"
                color="high-contrast"
                py="1-5x"
            >
                {formatLanguage(language)}
            </Label>
        );
    }

    const classes = cx("syntax-block-container", className);

    let playgroundButton;
    if (interactive) {
        playgroundButton = (
            <Button
                variation="ghost-primary"
                className="playground-button"
                size="md"
                iconLeft={<FaIcon icon={faSquareTerminal} />}
                onClick={handlePlayground}
            >
                Playground
            </Button>
        );
    }

    let copyLabel = "Copy";
    if (copyLabelShown) {
        copyLabel = "Copied";
    }

    return (
        <div className={classes} data-id={uniqueId}>
            <Flex
                className="block-header"
                background="code"
                alignItems="center"
                px="1-5x"
                py="1x"
                justifyContent="space-between"
            >
                <div className="language-container">{label}</div>

                <Flex className="toolbar" alignItems="center" gap="0-5x">
                    {playgroundButton}
                    <Button
                        variation="ghost-primary"
                        size="md"
                        iconLeft={<FaIcon icon={copyClipboardIcon} />}
                        onClick={handleCopy}
                    >
                        {copyLabel}
                    </Button>
                </Flex>
            </Flex>
            <Box className="block-content">
                {inputSection}
                {outputSection}
            </Box>
        </div>
    );
}
